import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Trophy, Flame, MapPin, Medal, Crown, Award, ChefHat, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// BARKA-based tabs only - NO money leaderboard
const TABS = [
  { id: 'barka', label: 'Top BARKA', icon: Star, color: 'text-orange-500' },
  { id: 'challengers', label: 'Champions', icon: Trophy, color: 'text-yellow-500' },
  { id: 'regions', label: 'Regions', icon: MapPin, color: 'text-blue-500' },
];

const RANK_STYLES = {
  1: { bg: 'bg-gradient-to-r from-yellow-400 to-amber-500', icon: Crown },
  2: { bg: 'bg-gradient-to-r from-gray-300 to-gray-400', icon: Medal },
  3: { bg: 'bg-gradient-to-r from-orange-400 to-orange-600', icon: Award },
};

const LEVEL_COLORS = {
  apprenti: 'bg-gray-500', commis: 'bg-green-500', chef_de_partie: 'bg-blue-500',
  sous_chef: 'bg-purple-500', chef_de_cuisine: 'bg-orange-500', maitre_harissa: 'bg-red-500'
};

function UserRow({ item, index, activeTab }) {
  const rank = index + 1;
  const rankStyle = RANK_STYLES[rank];
  
  return (
    <Link to={'/u/' + item.username} key={item.id || index}>
      <Card className={'mb-3 hover:shadow-md transition-shadow ' + (rank <= 3 ? 'border-2 ' : '') + (rank === 1 ? 'border-yellow-400' : rank === 2 ? 'border-gray-300' : rank === 3 ? 'border-orange-400' : '')}>
        <CardContent className="p-4 flex items-center gap-4">
          <div className={'w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ' + (rankStyle ? rankStyle.bg : 'bg-gray-200 text-gray-600')}>
            {rankStyle ? <rankStyle.icon className="w-5 h-5" /> : <span className="text-sm">{rank}</span>}
          </div>
          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
            {item.profile_picture ? (
              <img src={item.profile_picture} alt={item.display_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><ChefHat className="w-6 h-6 text-gray-400" /></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold truncate">{item.display_name}</p>
              {item.is_verified && <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center"><span className="text-white text-xs">✓</span></span>}
            </div>
            <p className="text-sm text-gray-500">@{item.username}</p>
          </div>
          <div className="text-right">
            {activeTab === 'barka' && (
              <div>
                <p className="font-cairo text-xl font-bold text-orange-500">{(item.barka || 0).toLocaleString()}</p>
                <Badge className={LEVEL_COLORS[item.level] + ' text-white text-xs'}>{(item.level || 'apprenti').replace('_', ' ')}</Badge>
              </div>
            )}
            {activeTab === 'challengers' && (
              <div>
                <p className="font-cairo text-xl font-bold text-yellow-500">{item.challenges_won}</p>
                <p className="text-xs text-gray-500">victoires</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function RegionRow({ item, index }) {
  const rank = index + 1;
  const rankStyle = RANK_STYLES[rank];
  
  return (
    <Card className={'mb-3 ' + (rank <= 3 ? 'border-2 ' : '') + (rank === 1 ? 'border-yellow-400' : rank === 2 ? 'border-gray-300' : rank === 3 ? 'border-orange-400' : '')}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={'w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ' + (rankStyle ? rankStyle.bg : 'bg-gray-200 text-gray-600')}>
          {rankStyle ? <rankStyle.icon className="w-5 h-5" /> : <span className="text-sm">{rank}</span>}
        </div>
        <div className="flex-1"><p className="font-semibold capitalize">{(item._id || 'inconnu').replace('_', ' ')}</p></div>
        <div className="text-right">
          <p className="font-cairo text-xl font-bold text-blue-500">{item.recipe_count}</p>
          <p className="text-xs text-gray-500">recettes</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function LeaderboardPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('barka');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const res = await api.get('/leaderboard/' + activeTab);
        setLeaderboard(res.data.leaderboard || []);
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [activeTab, api]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="leaderboard-page">
      <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-6 pt-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8" />
            <h1 className="font-cairo text-3xl font-black">{t('ranking.title', 'Leaderboard')}</h1>
          </div>
          <p className="text-white/80">{t('ranking.subtitle', 'Community reputation and contribution')}</p>
        </div>
      </div>

      <div className="sticky top-0 bg-white border-b z-10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex overflow-x-auto gap-2 py-3">
            {TABS.map(tab => (
              <Button key={tab.id} variant={activeTab === tab.id ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTab(tab.id)} className={'whitespace-nowrap rounded-full ' + (activeTab === tab.id ? 'bg-red-500 hover:bg-red-600 text-white' : '')} data-testid={'tab-' + tab.id}>
                <tab.icon className={'w-4 h-4 mr-1.5 ' + (activeTab !== tab.id ? tab.color : '')} />{tab.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-3">{[1,2,3,4,5].map(i => <Card key={i} className="mb-3"><CardContent className="p-4 flex items-center gap-4"><div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" /><div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" /><div className="flex-1"><div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-2" /></div></CardContent></Card>)}</div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-12"><Trophy className="w-16 h-16 mx-auto mb-4 text-gray-300" /><p className="text-gray-500">{t('common.noData', 'No data yet')}</p></div>
        ) : activeTab === 'regions' ? (
          leaderboard.map((item, idx) => <RegionRow key={idx} item={item} index={idx} />)
        ) : (
          leaderboard.map((item, idx) => <UserRow key={item.id || idx} item={item} index={idx} activeTab={activeTab} />)
        )}
      </div>
    </div>
  );
}
